import { Dispatch, EnhancedStore } from '@reduxjs/toolkit';
import { getQueryString } from 'lib/utility';
import React, { useMemo, PropsWithChildren, useState } from 'react';
import { Routes, BrowserRouter } from 'react-router-dom';
import { createPagePortals } from './createPortals';
import { createRoutes } from './createRoutes';

export const VIEWPORT_MEDIA_QUERIES = {
    isLargeDesktop: '(min-width: 1400px)',
    isDesktop: '(min-width: 1024px) and (max-width: 1399px)',
    isTablet: '(min-width: 768px) and (max-width: 1023px)',
    isMobile: '(max-width: 767px)',
};

interface IGtxPageContainerProps {
    queryString: { [key: string]: string | number };
}

type IBaseGtxPage = {
    /**
     * Component to be shown once executed iniFunc
     */
    container: React.FC<IGtxPageContainerProps>;

    /**
     * If defined the component will wrap the container and the generated header component
     * Is useful to share context between container and headers
     */
    wrapper?: React.FC<PropsWithChildren<{}>>;
    /**
     * Used by react-router to show the component
     */
    path: string;
    /**
     * headers config object will contains all options and params
     * for the headers zone
     */
    header?:
        | {
              /**
               * A component rendered inside the page title header bar
               * the same component will be rendered in mobile and desktop bar
               * so the responsive view has to be handled inside the actions
               * component itself
               */
              actions?: React.FC;
              /**
               * if defined the backTo button will be shown on the desktop header
               */
              backUrl?: string;
              /**
               * If true will show the header
               */
              hideOnMobile: boolean;
              /**
               * A custom title different from menu label (which will be the rendered title otherwise)
               */
              customTitle?: string;
          }
        | false;
};

export type GtxPageWithStore = {
    /**
     * The redux store created with redux-toolkit
     */
    store: EnhancedStore;
    /**
     * Usually a function to initialize the app it will show a full page loader until the promise is resolved
     * and will forward a redux dispatch if a store is declared utility function
     */
    initFunc?: (dispatch: Dispatch<any>) => Promise<any>;
} & IBaseGtxPage;

export type GtxPageWithoutStore = {
    /**
     * Usually a function to initialize the app it will show a full page loader until the promise is resolved
     * and will forward a setInitialData function that will fill initialData value in GtxAppContext accessible with useGtxAppContext
     */
    initFunc?: (setInitialData: (data: unknown) => void) => Promise<any>;
} & IBaseGtxPage;

export type GtxReactPage = GtxPageWithoutStore | GtxPageWithStore;

export interface IGtxApp {
    pages: GtxReactPage[];
}

/**
 * Riceve nella prop pages un array di GtxReactPage
 * - Gestisce il mount del wrapper e del container
 * dentro un portal
 * - monta l'header sempre in portali,
 * - espone al wrapper ed al container un GtxContext con useGtxAppContext nel quale si trova
 * la funzione goBack ed ospiterà altre utility.
 * - Gestisce il routing con React Router
 * - Gestisce il loader a piena pagina mostrandolo durante l'esecuzione della funzione async initFunc
 * - Mostra la pagina di errore nel caso di errore durante il caricamento dell'initFunc
 * - Istanzia lo store di Redux con Redux Toolkit ed espone lo store Provider al wrapper, all'header e al Container
 * - Gestisce la visibilità ed il posizionamento responsive dell'header
 *
 * Per ulteriore documentazione vedi le docs per le singole property dell'interfaccia GtxReactPage
 * */
export const GtxApp: React.FunctionComponent<IGtxApp> = ({ pages }) => {
    const qsPayload = getQueryString();
    const [initialData, setInitialData] = useState<null | unknown>(null);

    const pagesPortals = useMemo(() => createPagePortals(pages), [pages]);

    const routes = createRoutes(
        pages,
        pagesPortals,
        setInitialData,
        initialData,
        qsPayload
    );

    return <BrowserRouter>{<Routes>{routes}</Routes>}</BrowserRouter>;
};
